<script>
  import {fileToJson} from 'root/shared/tools/xls.service';
  import {logError} from 'root/log/log';
  import {updateReadMessages} from 'root/messaging/user-messaging.store';
  import {prepareUploadedRows} from './gbta-file-upload.service'
  import {saveAsCsvFile} from 'root/shared/tools/csv-export.service';

  export let
    cancel,
    hide,
    matchFn,
    uploadFn;

  const
    PAGE_START = 0,
    PAGE_WORKING = 1,
    PAGE_FINISHED = 2,
    PAGE_ERROR = -1;

  let
    pageStatus = PAGE_START,
    uploads = [],
    uploadedFileName,
    progress = 0,
    generalError;

  function onFileSelected(ev){
    pageStatus = PAGE_WORKING;
    const file = ev.target.files[0];

    fileToJson(file)
      .then(prepareUploadedRows)
      .then(matchFn)
      .then(async (matches) => {
        uploadedFileName = file.name || 'unknown';
        uploads = matches;

        for(let i = 0, l = uploads.length; i < l; i++){
          const upload = uploads[i];
          if(upload.data){
            try {
              uploads[i] = {...upload, message: 'Uploading...'};
              // eslint-disable-next-line no-await-in-loop
              await uploadFn(upload.bid, upload.data);
              uploads[i] = {...upload, done: true, message: `Uploaded response with ${upload.propCodeKey}: ${upload.propCode}`};
            } catch(error){
              logError(error);
              const message = getErrorMessage(error);
              uploads[i] = {...upload, error: true, message: `Upload failed! ${message}`};
            }
          }
          progress = Math.ceil((i+1) / l * 100);
        }

        pageStatus = PAGE_FINISHED;
      })
      .catch(error => {
        logError(error);
        generalError = getErrorMessage(error);
        pageStatus = PAGE_ERROR;
        updateReadMessages();
      });
  }

  function exportUploadReport(){
    saveAsCsvFile(
      `Upload report for ${uploadedFileName}`,
      ['Bid ID', 'Upload Status', 'Hotel Name', 'Hotel Address', 'PropCode Match Key', 'PropCode Match Value', 'Message'],
      uploads.map(upload => ({
        'Bid ID': upload.id,
        'Upload Status': upload.done ? 'Uploaded' : (upload.error ? 'Error' : 'Unknown'),
        'Hotel Name': upload.hotelName,
        'Hotel Address': upload.hotelAddress,
        'PropCode Match Key': upload.propCodeKey,
        'PropCode Match Value': upload.propCode,
        'Message': upload.message,
      }))
    );
  }

  function getErrorMessage(error){
    return (error.data? error.data.message: error.message) || 'Undetermined error.';
  }

</script>

<div class="ModalContainer">
  {#if pageStatus !== PAGE_WORKING}
    <div role="button"
         class="Close"
         tabindex="-1"
         data-icon-before="close"
         on:click={cancel}
    >
    </div>
  {/if}

  <div class="Title"
       class:Error={pageStatus === PAGE_ERROR}
  >
    <slot name="title">Title</slot>
  </div>

  <div class="Content">
    {#if pageStatus === PAGE_START}
      <div class="Description">
        <slot name="description">Description</slot>
      </div>

    {:else if pageStatus === PAGE_ERROR}
      <div class="Description">
        <div><b>There was an unexpected error - Upload Failed!</b></div>
        <div>The error message is: <i>{generalError}</i></div>
      </div>

    {:else if pageStatus === PAGE_WORKING || pageStatus === PAGE_FINISHED}
      {#if uploads.length}
        <div class="UploadReport">
          {#each uploads as upload (upload.id)}
            <div class="Upload">
              <div class="UploadDetails">
                <div class="ellipsis"><b>{upload.hotelName}</b></div>
                <div class="ellipsis"><small>{upload.hotelAddress}</small></div>

                <div class="Message" class:Error="{upload.error}">{upload.message}</div>
              </div>

              <div class="UploadStatus"
                   class:Error={upload.error}
                   class:Done={upload.done}
              >
              </div>
            </div>
          {/each}
        </div>

      {:else}
        <div class="Description">Loading file, please wait...</div>
      {/if}
    {/if}
  </div>

  <div class="Footer">
    {#if pageStatus === PAGE_START}
      <div>
        <label for="fileUploader" class="aBtn atLight asLarge asMain">
          <i class="material-icons">file_upload</i><span>Upload File</span>
        </label>

        <input type="file"
               id="fileUploader"
               accept=".csv, .txt, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
               class="hidden"
               on:change={onFileSelected}
        />
      </div>
      <button type="button" class="aBtn atLight asLarge" on:click={cancel}>Cancel</button>

    {:else if pageStatus === PAGE_ERROR}
      <button type="button" class="aBtn atLight asLarge" on:click={cancel}>Close</button>

    {:else if pageStatus === PAGE_WORKING}
      <div class="UploadProgressBar">
        <div style="width: {progress}%"></div>
      </div>

    {:else if pageStatus === PAGE_FINISHED}
      <button type="button"
              class="aBtn atLight asLarge"
              on:click={exportUploadReport}
      >
        Get Upload Report
      </button>

      <button type="button"
              class="aBtn atLight asLarge asMain"
              on:click={hide}
      >
        Close
      </button>
    {/if}
  </div>
</div>

<style lang="stylus">

  .ModalContainer {
    min-width: 600px;
    width: 600px;
    max-width: 80vw;
    position: relative;
    box-shadow: 0 5px 24px 0 rgba(0, 0, 0, 0.5);
    background: white;
    display: flex;
    flex-direction: column;

    color: #455A64;
    font-size: 13px;
  }

  .Close {
    cursor: pointer;

    position: absolute;
    top: 10px;
    right: 5px;
    opacity: 1;
    transition: opacity 0.3s;

    &:before {
      font-size: 20px;
      color: white;
    }

    &:hover {
      opacity: 0.7;
    }
  }

  .Title {
    flex: 0 0 auto;
    text-align: center;
    font-size: 16px;
    line-height: 40px;
    background: #00a99d;
    color: #fff;
    font-weight: bold;

    &.Error {
      background: #F15A24;
      color: #fff;
    }
  }

  .Content {
    padding: 0 13px;
    color: #546E7A;
    font-size: 13px;
    margin: 10px 0 20px;
    max-height: 60vh;
    overflow: auto;
    position: relative;
  }

  .Description {
    :global(div) {
      margin: 10px 0;
    }
  }

  .Footer {
    padding: 0 18px 21px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .UploadProgressBar {
    background: #fff;
    border: 1px solid #00a99d;
    width: 100%;
    border-radius: 6px;
    overflow: hidden;

    div {
      height: 10px;
      background: #00a99d;
      transition: width 2s linear;
    }
  }

  label {
    display: flex;
    align-items: center;
    margin-right: 10px;
  }

  .Upload {
    display: flex;
    gap: 10px;
    align-items: center;
    border-bottom: 1px solid #B0BEC5;
    padding: 10px;
  }

  .UploadDetails {
    flex: 1 1 100%;
    overflow: hidden;

    .Message {
      margin-top: 5px;
    }

    .Error {
      color: #d75539;
    }
  }

  .UploadStatus {
    flex: 0 0 40px;
    align-items: center;
    justify-content: center;

    &:before {
      content: 'pending';
      font-family: 'Material Icons';
      font-weight: normal;
      font-style: normal;
      font-size: 24px;  /* Preferred icon size */
      display: inline-block;
      line-height: 1;
      text-transform: none;
      letter-spacing: normal;
      word-wrap: normal;
      white-space: nowrap;
      direction: ltr;

      /* Support for all WebKit browsers. */
      -webkit-font-smoothing: antialiased;
      /* Support for Safari and Chrome. */
      text-rendering: optimizeLegibility;

      /* Support for Firefox. */
      -moz-osx-font-smoothing: grayscale;

      /* Support for IE. */
      font-feature-settings: 'liga';
    }

    &.Error {
      color: #d75539;

      &:before {
        content: 'error';
      }
    }

    &.Done {
      color: #00a99d;

      &:before {
        content: 'done';
      }
    }
  }

</style>
